import { useMutation } from '@tanstack/react-query';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { AiLimitErrorMessage } from '../aiUsage/AiLimitNotice';
import { getAiLimitErrorKind } from '../../services/aiUsageService';
import { analyzeJournalEntry } from '../../services/journalAiService';
import { useTranslation } from '../../i18n/useTranslation';
import { formatGeneratedAt } from './formatGeneratedAt';
import './JournalAi.css';

export interface JournalEntryAiAnalysisProps {
  aquariumId: string;
  entryId: string;
}

/**
 * "AI analysis" of one journal entry — runs only when the user clicks the
 * button (one metered AI call), answer in the UI language. Not stored.
 */
export function JournalEntryAiAnalysis({ aquariumId, entryId }: JournalEntryAiAnalysisProps) {
  const { t, i18n } = useTranslation();
  const mutation = useMutation({ mutationFn: () => analyzeJournalEntry(aquariumId, entryId) });
  const result = mutation.data;
  const limitError = mutation.error != null && getAiLimitErrorKind(mutation.error) !== null;

  const lists: Array<{ key: string; title: string; items: string[] }> = result
    ? [
        { key: 'observations', title: t('journal.ai.observations'), items: result.observations },
        { key: 'actions', title: t('journal.ai.actions'), items: result.suggestedActions },
        { key: 'risks', title: t('journal.ai.risks'), items: result.riskIndicators },
      ].filter((l) => l.items.length > 0)
    : [];

  return (
    <div className="rg-journal-ai" data-testid="journal-entry-ai-analysis">
      <Text color="secondary">{t('journal.ai.description')}</Text>
      <div className="rg-journal-ai__actions">
        <Button
          type="button"
          variant="secondary"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          isLoading={mutation.isPending}
        >
          {mutation.isPending ? t('journal.ai.running') : result ? t('journal.ai.rerun') : t('journal.ai.run')}
        </Button>
      </div>
      <LiveRegion visuallyHidden busy={mutation.isPending}>
        {mutation.isPending ? t('journal.ai.running') : result ? t('journal.ai.done') : ''}
      </LiveRegion>
      {mutation.isError && (limitError
        ? <AiLimitErrorMessage error={mutation.error} />
        : <Alert tone="danger">{t('journal.ai.failed')}</Alert>)}
      {result && (
        <div className="rg-journal-ai__result" data-testid="journal-entry-ai-result">
          <Heading level={3} variant="subsection">{t('journal.ai.summary')}</Heading>
          <Text>{result.summary}</Text>
          {lists.map((list) => (
            <div key={list.key} className="rg-journal-ai__list-block">
              <Heading level={4} variant="label">{list.title}</Heading>
              <ul className="rg-journal-ai__list">
                {list.items.map((item, index) => (
                  <li key={index}><Text as="span">{item}</Text></li>
                ))}
              </ul>
            </div>
          ))}
          <Text size="sm" color="muted">
            {t('journal.ai.generatedAt', { date: formatGeneratedAt(result.generatedAt, i18n.language) })}
            {' · '}
            {t('journal.ai.disclaimer')}
          </Text>
        </div>
      )}
    </div>
  );
}

export default JournalEntryAiAnalysis;
